import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import API_BASE_URL from './config/api';
import Spinner from 'react-bootstrap/Spinner';
import { FaBuilding, FaSearch, FaExternalLinkAlt, FaSortUp, FaSortDown, FaSort } from 'react-icons/fa';

function daysSince(dateStr) {
  if (!dateStr) return null;
  try {
    const d = new Date(dateStr.includes('T') ? dateStr : dateStr + 'T00:00:00');
    const now = new Date();
    now.setHours(0, 0, 0, 0);
    const diff = Math.floor((now - d) / (1000 * 60 * 60 * 24));
    return diff >= 0 ? diff : 0;
  } catch { return null; }
}

function daysLabel(days, compact) {
  if (days === null) return compact ? 'None yet' : 'No visits yet';
  if (days === 0) return 'Today';
  if (days === 1) return compact ? '1d ago' : '1 day ago';
  return compact ? `${days}d ago` : `${days} days ago`;
}

function daysColor(days) {
  if (days === null) return '#9ca3af';
  if (days <= 30) return '#10b981';
  if (days <= 60) return '#f59e0b';
  return '#ef4444';
}

function shortDate(dateStr) {
  if (!dateStr) return '';
  try {
    const d = new Date(dateStr.includes('T') ? dateStr : dateStr + 'T00:00:00');
    return d.toLocaleDateString('en-CA', { month: 'short', day: 'numeric', timeZone: 'America/Toronto' });
  } catch { return ''; }
}

function nextVisitInfo(startAt) {
  if (!startAt) return null;
  try {
    const d = new Date(startAt);
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const visitDay = new Date(d.getFullYear(), d.getMonth(), d.getDate());
    const diff = Math.round((visitDay - today) / (1000 * 60 * 60 * 24));
    const date = d.toLocaleDateString('en-CA', { month: 'short', day: 'numeric', timeZone: 'America/Toronto' });
    if (diff === 0) return { rel: 'Today', relShort: 'Today', date };
    if (diff === 1) return { rel: 'Tomorrow', relShort: 'Tmrw', date };
    return { rel: `In ${diff} days`, relShort: `In ${diff}d`, date };
  } catch { return null; }
}

// Responsive sizing lives in CSS media queries (not JS viewport checks)
// so it always follows the real device viewport. The short/long label
// variants are both rendered and toggled by the media query.
const RESPONSIVE_CSS = `
  .mech-page { padding: 1.5rem 1.25rem; }
  .mech-tbl { width: 100%; border-collapse: collapse; table-layout: fixed; }
  .mech-tbl th { padding: 0.85rem 1rem; font-size: 0.8rem; }
  .mech-tbl td { padding: 1rem; }
  .mech-col-date { width: 150px; }
  .mech-lbl-short { display: none; }
  @media (max-width: 640px) {
    .mech-page { padding: 1.25rem 0.6rem; }
    .mech-tbl th { padding: 0.85rem 0.5rem; font-size: 0.72rem; }
    .mech-tbl td { padding: 1rem 0.5rem; }
    .mech-col-date { width: 90px; }
    .mech-lbl-full { display: none; }
    .mech-lbl-short { display: inline; }
  }
`;

const thBase = {
  fontWeight: 700, color: '#6b7280',
  textTransform: 'uppercase', letterSpacing: '0.04em', textAlign: 'left', whiteSpace: 'nowrap',
  borderBottom: '1px solid #e5e7eb', background: '#f9fafb', userSelect: 'none',
};

function SortHeader({ label, shortLabel, active, dir, onClick }) {
  return (
    <th onClick={onClick} style={{ ...thBase, cursor: 'pointer', color: active ? '#2563eb' : '#6b7280' }}>
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.25rem' }}>
        {shortLabel ? (
          <>
            <span className="mech-lbl-full">{label}</span>
            <span className="mech-lbl-short">{shortLabel}</span>
          </>
        ) : label}
        {active ? (dir === 'asc' ? <FaSortUp /> : <FaSortDown />) : <FaSort style={{ opacity: 0.35 }} />}
      </span>
    </th>
  );
}

export default function MechScheduleHome() {
  const [buildings, setBuildings] = useState([]);
  const [upcoming, setUpcoming] = useState({});
  const [search, setSearch] = useState('');
  const [sortKey, setSortKey] = useState('last');   // 'name' | 'last' | 'next'
  const [sortDir, setSortDir] = useState('asc');    // 'last' asc = oldest first, 'next' asc = soonest first
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setIsLoading(true);
      try {
        const r = await fetch(`${API_BASE_URL}/api/mechanical/tech/buildings`);
        if (!r.ok) throw new Error(`Failed to load buildings: ${r.status}`);
        const data = await r.json();
        if (!cancelled) {
          setBuildings(Array.isArray(data.buildings) ? data.buildings : []);
          setError(null);
        }
      } catch (e) {
        console.error('[MechScheduleHome] Error:', e);
        if (!cancelled) { setBuildings([]); setError('Failed to load buildings. Please check your connection and try again.'); }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    }
    load();

    // Upcoming Jobber visits load separately so the list never blocks on Jobber
    (async () => {
      try {
        const r = await fetch(`${API_BASE_URL}/api/mechanical/jobber/upcoming`);
        if (!r.ok) return;
        const data = await r.json();
        if (!cancelled && data && data.buildings) setUpcoming(data.buildings);
      } catch (e) {
        console.warn('[MechScheduleHome] Upcoming visits unavailable:', e);
      }
    })();

    return () => { cancelled = true; };
  }, []);

  const toggleSort = (key) => {
    if (sortKey === key) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
  };

  const rows = useMemo(() => {
    let list = buildings
      .filter((b) => {
        if (!search.trim()) return true;
        const q = search.toLowerCase();
        return (b.name || '').toLowerCase().includes(q) || (b.address || '').toLowerCase().includes(q) || (b.buildingId || '').toLowerCase().includes(q);
      })
      .map((b) => {
        const up = upcoming[b.id];
        const nextVisit = up && up.visits && up.visits.length > 0 ? up.visits[0] : null;
        return {
          ...b,
          job: up && up.job ? up.job : null,
          nextStartAt: nextVisit ? nextVisit.startAt : null,
        };
      });

    const flip = sortDir === 'asc' ? 1 : -1;
    list.sort((a, b) => {
      if (sortKey === 'name') return flip * (a.name || '').localeCompare(b.name || '');
      const kA = sortKey === 'last' ? (a.lastVisitDate || '') : (a.nextStartAt || '');
      const kB = sortKey === 'last' ? (b.lastVisitDate || '') : (b.nextStartAt || '');
      // Blanks: "no visits yet" floats to top for last-visit oldest-first; "no next visit" always sinks
      if (!kA && !kB) return (a.name || '').localeCompare(b.name || '');
      if (!kA) return sortKey === 'last' ? -flip : 1;
      if (!kB) return sortKey === 'last' ? flip : -1;
      return flip * kA.localeCompare(kB);
    });

    return list;
  }, [buildings, upcoming, search, sortKey, sortDir]);

  return (
    <div className="mech-page" style={{ minHeight: '100vh', background: '#f9fafb' }}>
      <style>{RESPONSIVE_CSS}</style>
      <div style={{ maxWidth: 1000, margin: '0 auto' }}>
        <h1 style={{ fontSize: '2rem', fontWeight: 700, color: '#111827', marginBottom: '0.25rem' }}>
          Mechanical Room Schedule
        </h1>
        <p style={{ fontSize: '1.05rem', color: '#6b7280', marginBottom: '1.5rem' }}>
          Buildings with active mechanical room contracts
        </p>

        {/* Search */}
        <div style={{ position: 'relative', marginBottom: '1.25rem' }}>
          <FaSearch style={{ position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)', color: '#9ca3af', fontSize: '0.9rem' }} />
          <input
            type="text" placeholder="Search buildings..." value={search} onChange={(e) => setSearch(e.target.value)}
            style={{ width: '100%', padding: '0.8rem 0.75rem 0.8rem 2.5rem', border: '1px solid #e5e7eb', borderRadius: '10px', fontSize: '1.05rem', outline: 'none', background: '#fff' }}
          />
        </div>

        {isLoading && (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '3rem 1rem' }}>
            <Spinner animation="border" style={{ color: '#2563eb', marginBottom: '1rem' }}><span className="visually-hidden">Loading...</span></Spinner>
            <p style={{ color: '#6b7280', fontWeight: 500, margin: 0 }}>Loading buildings...</p>
          </div>
        )}

        {error && (
          <div style={{ padding: '1rem', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '0.5rem', color: '#991b1b', fontSize: '0.9rem', marginBottom: '1rem' }}>{error}</div>
        )}

        {!isLoading && !error && rows.length === 0 && (
          <div style={{ textAlign: 'center', padding: '3rem 1rem', color: '#6b7280' }}>
            {search ? 'No buildings match your search.' : 'No buildings with mechanical room contracts found.'}
          </div>
        )}

        {!isLoading && !error && rows.length > 0 && (
          <div style={{ background: '#fff', borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.08)' }}>
            {/* Fixed layout so the building name truncates instead of pushing
                the visit columns off-screen on phones */}
            <table className="mech-tbl">
              <colgroup>
                <col />
                <col className="mech-col-date" />
                <col className="mech-col-date" />
              </colgroup>
              <thead>
                <tr>
                  <SortHeader label="Building" active={sortKey === 'name'} dir={sortDir} onClick={() => toggleSort('name')} />
                  <SortHeader label="Last visit" shortLabel="Last" active={sortKey === 'last'} dir={sortDir} onClick={() => toggleSort('last')} />
                  <SortHeader label="Next visit" shortLabel="Next" active={sortKey === 'next'} dir={sortDir} onClick={() => toggleSort('next')} />
                </tr>
              </thead>
              <tbody>
                {rows.map((b) => {
                  const days = daysSince(b.lastVisitDate);
                  const next = nextVisitInfo(b.nextStartAt);
                  return (
                    <tr
                      key={b.id}
                      onClick={() => navigate(`/mech-schedule/buildings/${encodeURIComponent(b.id)}`)}
                      style={{ cursor: 'pointer', borderBottom: '1px solid #f3f4f6', transition: 'background 0.12s ease' }}
                      onMouseEnter={(e) => { e.currentTarget.style.background = '#f0f6ff'; }}
                      onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                    >
                      <td style={{ overflow: 'hidden' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.7rem', minWidth: 0 }}>
                          {b.photoUrl ? (
                            <img src={b.photoUrl} alt="" loading="lazy" decoding="async"
                              style={{ width: 48, height: 48, borderRadius: 11, objectFit: 'cover', flexShrink: 0, background: '#f3f4f6' }}
                              onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }} />
                          ) : null}
                          <div style={{ width: 48, height: 48, borderRadius: 11, background: '#eff6ff', display: b.photoUrl ? 'none' : 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                            <FaBuilding style={{ fontSize: '1.25rem', color: '#3b82f6' }} />
                          </div>
                          <div style={{ minWidth: 0 }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', minWidth: 0 }}>
                              <div style={{ fontWeight: 600, color: '#111827', fontSize: '1.05rem', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{b.name}</div>
                              {b.job && b.job.webUri && (
                                <a
                                  href={b.job.webUri}
                                  target="_blank"
                                  rel="noreferrer"
                                  onClick={(e) => e.stopPropagation()}
                                  title={`Open ${b.job.title || 'master job'} in Jobber`}
                                  style={{
                                    display: 'inline-flex', alignItems: 'center', gap: '0.3rem', flexShrink: 0,
                                    fontSize: '0.72rem', fontWeight: 700, color: '#2563eb', textDecoration: 'none',
                                    background: '#eff6ff', border: '1px solid #dbeafe', borderRadius: '999px',
                                    padding: '0.12rem 0.55rem', cursor: 'pointer',
                                  }}
                                >
                                  <span className="mech-lbl-full">Jobber</span> <FaExternalLinkAlt style={{ fontSize: '0.55rem' }} />
                                </a>
                              )}
                            </div>
                            {b.address && (
                              <div style={{ fontSize: '0.85rem', color: '#6b7280', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', marginTop: '0.15rem' }}>{b.address}</div>
                            )}
                            {/* Amber rather than red: an open issue is normal
                                on a mechanical contract, so this is a workload
                                figure, not an alarm. Buildings with none stay
                                silent so the ones carrying work stand out. */}
                            {b.openIssues > 0 && (
                              <div style={{ fontSize: '0.8rem', color: '#b45309', fontWeight: 600, marginTop: '0.2rem', whiteSpace: 'nowrap' }}>
                                {b.openIssues} unresolved {b.openIssues === 1 ? 'issue' : 'issues'}
                              </div>
                            )}
                          </div>
                        </div>
                      </td>
                      <td style={{ whiteSpace: 'nowrap', overflow: 'hidden' }}>
                        <div style={{ fontSize: '1rem', fontWeight: 600, color: daysColor(days) }}>
                          <span className="mech-lbl-full">{daysLabel(days, false)}</span>
                          <span className="mech-lbl-short">{daysLabel(days, true)}</span>
                        </div>
                        {b.lastVisitDate && <div style={{ fontSize: '0.8rem', color: '#9ca3af', marginTop: '0.15rem' }}>{shortDate(b.lastVisitDate)}</div>}
                      </td>
                      <td style={{ whiteSpace: 'nowrap', overflow: 'hidden' }}>
                        {next ? (
                          <>
                            <div style={{ fontSize: '1rem', fontWeight: 600, color: '#2563eb' }}>
                              <span className="mech-lbl-full">{next.rel}</span>
                              <span className="mech-lbl-short">{next.relShort}</span>
                            </div>
                            <div style={{ fontSize: '0.8rem', color: '#9ca3af', marginTop: '0.15rem' }}>{next.date}</div>
                          </>
                        ) : (
                          <span style={{ fontSize: '0.9rem', color: '#d1d5db' }}>—</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
